import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAdminOrders,
  useCompleteOrder,
  useCancelOrder,
} from "@/hooks/useAdminOrders";
import { OrderFilters, OrderListTable } from "@/components/orders";
import { usePermissions } from "@/hooks/usePermissions";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, RefreshCw, ShoppingBag } from "lucide-react";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import { OrderAdminListItem } from "@/services/admin-order.service";
import { formatDateRangeForAPI } from "@/lib/date-utils";
import { ALL_STATUSES, useOrderFiltersStore } from "@/stores/orderFiltersStore";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getPaginationItems } from "@/lib/pagination";

export function AdminOrdersListPage() {
  const navigate = useNavigate();

  // Filters, page and page size live in a persisted store, so the view an operator set up survives a
  // reload and a trip to an order's detail screen and back. The store also owns the "any filter
  // change returns to page 1" rule.
  const { keyword, status, dateRange } = useOrderFiltersStore(
    (state) => state.filters,
  );
  const page = useOrderFiltersStore((state) => state.page);
  const pageSize = useOrderFiltersStore((state) => state.pageSize);
  const setPage = useOrderFiltersStore((state) => state.setPage);
  const setPageSize = useOrderFiltersStore((state) => state.setPageSize);
  const clearCache = useOrderFiltersStore((state) => state.clearCache);
  const hasActiveFilters = useOrderFiltersStore((state) =>
    state.hasActiveFilters(),
  );

  // Both ends or neither: the picker never produces half a range, and half a range narrows nothing
  // while still rendering an active-filter chip.
  const { fromDate, toDate } =
    dateRange?.from && dateRange?.to
      ? formatDateRangeForAPI(dateRange.from, dateRange.to, false)
      : { fromDate: undefined, toDate: undefined };

  // No `tenantId` parameter any more. The one search box answers a pasted tenant id: the backend
  // routes the term by shape and matches a GUID against the order, its tenant and the user who
  // placed it. That also retires the rewrite this page used to do — a non-GUID typed into the old
  // tenant box was sent as Guid.Empty so the backend would answer nothing, which is a strange way
  // to tell an operator that what they typed is not an id.
  const filters = {
    page,
    pageSize,
    keyword: keyword?.trim() || undefined,
    status: status !== ALL_STATUSES ? status : undefined,
    fromDate,
    toDate,
  };

  // Fetch data
  const {
    data: ordersData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useAdminOrders(filters);

  // Mutations
  // Complete/cancel are SuperAdmin+BillingAdmin. OrderListTable hides the entries when the
  // handlers are absent, so withholding them is the gate.
  const { hasRole } = usePermissions();
  const canSettleOrder = hasRole(["SuperAdmin", "BillingAdmin"]);
  const completeOrderMutation = useCompleteOrder();
  const cancelOrderMutation = useCancelOrder();

  // Dialog states
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderAdminListItem | null>(
    null,
  );
  const [completePaymentId, setCompletePaymentId] = useState("");
  const [completeInvoiceId, setCompleteInvoiceId] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  const handleCompleteOrder = (order: OrderAdminListItem) => {
    setSelectedOrder(order);
    setCompletePaymentId("");
    setCompleteInvoiceId("");
    setCompleteDialogOpen(true);
  };

  const handleCancelOrder = (order: OrderAdminListItem) => {
    setSelectedOrder(order);
    setCancelReason("");
    setCancelDialogOpen(true);
  };

  const submitCompleteOrder = () => {
    if (!selectedOrder || !completePaymentId || !completeInvoiceId) return;

    completeOrderMutation.mutate(
      {
        orderId: selectedOrder.id,
        paymentId: completePaymentId,
        invoiceId: completeInvoiceId,
      },
      {
        onSuccess: () => {
          setCompleteDialogOpen(false);
          setSelectedOrder(null);
        },
      },
    );
  };

  const submitCancelOrder = () => {
    if (!selectedOrder || !cancelReason) return;

    cancelOrderMutation.mutate(
      {
        orderId: selectedOrder.id,
        reason: cancelReason,
      },
      {
        onSuccess: () => {
          setCancelDialogOpen(false);
          setSelectedOrder(null);
        },
      },
    );
  };

  const handleViewDetails = (orderId: string) => {
    navigate(`/admin/orders/${orderId}`);
  };

  // Memoize orders data to prevent unnecessary re-renders
  const orders = useMemo(() => ordersData?.data || [], [ordersData?.data]);
  const pagination = useMemo(
    () => ordersData?.pagination,
    [ordersData?.pagination],
  );
  const totalPages = pagination?.totalPages ?? 1;
  const totalItems = pagination?.totalItems ?? 0;

  // Only take the screen over when there is genuinely nothing to show. A refetch that fails - a
  // token refresh losing a race, a blip on one filter change - used to replace the table AND the
  // whole filter bar, so the operator had to rebuild their filters to get back to rows that were
  // still on screen a moment earlier. With a response in hand the failure is reported in a banner
  // above the table instead, and Retry is one click away either way.
  if (error && !ordersData) {
    return <ErrorFallback error={error as Error} onRetry={() => refetch()} />;
  }

  // This screen's "start over": drops every filter, the page size and the cached copy in
  // sessionStorage, then reloads. The search box holds its own copy of what is being typed and
  // re-reads the store on change, so clearing the store empties it too.
  const handleRefresh = () => {
    clearCache();
    void refetch();
  };

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {/* Anchored to the Card, not the viewport: `fixed inset-0` centred this on the screen, so
            on a short table it hovered in blank space well below the rows it described.

            `!isLoading` keeps the two loading signals mutually exclusive — skeleton rows while
            there is nothing to show, the pill only while refreshing rows the operator can still
            see. Both at once said "Updating..." over a table that had never shown anything. */}
        {isFetching && !isLoading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Updating...</span>
            </div>
          </div>
        )}

        {/* Row writes cover the table's Card, not the page: it is the table that must not take a
            second write before the re-read lands. */}
        <BusyOverlay
          contained
          open={
            completeOrderMutation.isPending || cancelOrderMutation.isPending
          }
          label={
            completeOrderMutation.isPending
              ? "Completing order…"
              : "Cancelling order…"
          }
        />

        <CardHeader className="shrink-0 p-2">
          <OrderFilters
            onProvisionVm={() =>
              navigate("/admin/resources/provision-for-user")
            }
          />
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          {/* Reached only with a previous result in hand - the no-data case returned above. A
              failed background refetch says so here and leaves the table and the filter bar
              alone. */}
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}
          {isLoading ? (
            /* Skeleton rows inside the table component, not a centred spinner: the spinner was as
               tall as its own content, so the card collapsed on the first load and grew again when
               the rows arrived. */
            <OrderListTable
              orders={[]}
              onViewDetails={handleViewDetails}
              isLoading
            />
          ) : orders.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              <ShoppingBag className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                {hasActiveFilters ? "No results found" : "No orders yet"}
              </h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? "Try adjusting or clearing your filters."
                  : "Orders will appear here once tenants place them."}
              </p>
            </div>
          ) : (
            <OrderListTable
              orders={orders}
              pagination={pagination}
              onViewDetails={handleViewDetails}
              {...(canSettleOrder
                ? {
                    onComplete: handleCompleteOrder,
                    onCancel: handleCancelOrder,
                  }
                : {})}
            />
          )}

          {/* Outside the three branches above, so a search that matched nothing still shows its
              pager, its page-size picker and the reload button. It reads "Page 1 of 1 (0 total)"
              with both arrows disabled - a true statement about a result that came back, and the
              row an operator needs to change the page size or step back after a filter emptied the
              list. `!isLoading` is the one case held back: with no answer yet, "0 total" would
              describe a request still in flight. */}
          {!isLoading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({totalItems} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => setPageSize(parseInt(value))}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                {/* Rendered at every page count, not just `totalPages > 1`: hiding the pager on a
                    single page made the controls appear and disappear as filters changed. */}
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1 || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(page, totalPages).map((item, index) =>
                    item === "ellipsis" ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-1 text-sm text-muted-foreground"
                      >
                        …
                      </span>
                    ) : (
                      <Button
                        key={item}
                        onClick={() => setPage(item as number)}
                        disabled={isFetching}
                        variant={page === item ? "default" : "outline"}
                        size="sm"
                        className="min-w-9"
                      >
                        {item}
                      </Button>
                    ),
                  )}
                  <Button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>

                {/* Sits with the other paging controls, the same place every other admin list keeps
                    it. Icon only, and the tooltip says what the click actually does: this is the
                    screen's "start over", so a caption reading "Refresh" would promise a reload
                    while quietly emptying the filter bar. */}
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Complete Order Dialog */}
      <Dialog
        open={completeDialogOpen}
        onOpenChange={(next) => {
          // The mutation stays open (see submitCompleteOrder) until its onSettled invalidation
          // lands, so a request in flight owns the dialog — Escape/X/outside click must not
          // unmount it out from under a write that has no way to report back.
          if (completeOrderMutation.isPending) return;
          setCompleteDialogOpen(next);
        }}
      >
        <DialogContent
          onEscapeKeyDown={(event) => {
            if (completeOrderMutation.isPending) event.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Complete Order</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <DialogDescription>
              Manually mark order {selectedOrder?.orderNumber} as complete.
            </DialogDescription>
            <div className="space-y-2">
              <Label htmlFor="paymentId">Payment ID</Label>
              <Input
                id="paymentId"
                placeholder="Enter payment ID"
                value={completePaymentId}
                onChange={(e) => setCompletePaymentId(e.target.value)}
                disabled={completeOrderMutation.isPending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoiceId">Invoice ID</Label>
              <Input
                id="invoiceId"
                placeholder="Enter invoice ID"
                value={completeInvoiceId}
                onChange={(e) => setCompleteInvoiceId(e.target.value)}
                disabled={completeOrderMutation.isPending}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCompleteDialogOpen(false)}
              disabled={completeOrderMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={submitCompleteOrder}
              disabled={
                !completePaymentId ||
                !completeInvoiceId ||
                completeOrderMutation.isPending
              }
            >
              {completeOrderMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {completeOrderMutation.isPending
                ? "Completing..."
                : "Complete Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onOpenChange={(next) => {
          if (cancelOrderMutation.isPending) return;
          setCancelDialogOpen(next);
        }}
      >
        <DialogContent
          onEscapeKeyDown={(event) => {
            if (cancelOrderMutation.isPending) event.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Cancel Order</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <DialogDescription>
              Cancel order {selectedOrder?.orderNumber}. Its open payment is
              cancelled, any reserved machines are released, and the invoice is
              voided — no money is refunded. The reason is shown to the
              customer.
            </DialogDescription>
            <div className="space-y-2">
              <Label htmlFor="reason">Cancellation Reason</Label>
              <Textarea
                id="reason"
                placeholder="Enter reason for cancellation..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
                disabled={cancelOrderMutation.isPending}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelOrderMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitCancelOrder}
              disabled={!cancelReason || cancelOrderMutation.isPending}
            >
              {cancelOrderMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {cancelOrderMutation.isPending ? "Cancelling..." : "Cancel Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
